import React from 'react';
import { useDashboardCombustivel } from 'src/controllers/Dashboard/FleetAdmin/DashboardCombustivelController';
import ConsumoSemanalChart from './ConsumoSemanalChart';

const fmtBRL = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtL = (v: number) =>
    `${v.toLocaleString('pt-BR')} L`;

const cardStyle: React.CSSProperties = {
    background: '#1e1e2e',
    borderRadius: 16,
    padding: '1.2rem 1.4rem',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minHeight: 130,
    position: 'relative',
};

const badgeStyle = (color: string): React.CSSProperties => ({
    position: 'absolute',
    top: 12,
    right: 12,
    background: color,
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
});

const iconStyle: React.CSSProperties = {
    fontSize: 22,
    marginBottom: 4,
};

const titleStyle: React.CSSProperties = {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#a0aec0',
    marginTop: 4,
};

const mainStyle: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.1,
};

const subStyle: React.CSSProperties = {
    fontSize: 12,
    color: '#a0aec0',
};

const DashboardCombustivelCards: React.FC = () => {
    const { data, loading } = useDashboardCombustivel();

    if (loading) {
        return (
            <div className="p-grid p-fluid" style={{ marginBottom: '1.5rem' }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="p-col-12 p-md-3">
                        <div style={{ ...cardStyle, justifyContent: 'center', alignItems: 'center' }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5em' }} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const { abastecimentoHoje, veiculosHoje, mesAtual, alertas } = data;
    const percColor = abastecimentoHoje.percMudanca >= 0 ? '#ef4444' : '#22c55e';
    const percLabel = `${abastecimentoHoje.percMudanca >= 0 ? '↑' : '↓'} ${Math.abs(abastecimentoHoje.percMudanca)}%`;

    return (
        <>
        <div className="p-grid p-fluid" style={{ marginBottom: '1rem' }}>
            {/* Card 1 — Abastecimento hoje */}
            <div className="p-col-12 p-md-3">
                <div style={cardStyle}>
                    <span style={badgeStyle(percColor)}>{percLabel}</span>
                    <span style={iconStyle}>⛽</span>
                    <span style={titleStyle}>Abastec. Hoje</span>
                    <span style={mainStyle}>{fmtL(abastecimentoHoje.litros)}</span>
                    <span style={subStyle}>{abastecimentoHoje.litrosPorHora} litros/hora</span>
                </div>
            </div>

            {/* Card 2 — Veículos hoje */}
            <div className="p-col-12 p-md-3">
                <div style={cardStyle}>
                    <span style={iconStyle}>🚗</span>
                    <span style={titleStyle}>Veículos Hoje</span>
                    <span style={mainStyle}>{veiculosHoje.hoje}</span>
                    <span style={subStyle}>de {veiculosHoje.totalFrota} na frota</span>
                </div>
            </div>

            {/* Card 3 — Mês atual */}
            <div className="p-col-12 p-md-3">
                <div style={cardStyle}>
                    <span style={iconStyle}>📅</span>
                    <span style={titleStyle}>Mês Atual</span>
                    <span style={mainStyle}>{fmtL(mesAtual.litros)}</span>
                    <span style={subStyle}>{fmtBRL(mesAtual.valor)}</span>
                </div>
            </div>

            {/* Card 4 — Alertas */}
            <div className="p-col-12 p-md-3">
                <div style={cardStyle}>
                    {alertas.criticos > 0 && (
                        <span style={badgeStyle('#ef4444')}>{alertas.criticos} críticos</span>
                    )}
                    <span style={iconStyle}>⚠️</span>
                    <span style={titleStyle}>Alertas Ativos</span>
                    <span style={mainStyle}>{alertas.total}</span>
                    <span style={subStyle}>{alertas.criticos} críticos</span>
                </div>
            </div>
        </div>
        <ConsumoSemanalChart consumoSemanal={data.consumoSemanal} />
        </>
    );
};

export default DashboardCombustivelCards;
